// "You have unsaved changes" gate shown by the multi-tabular entry grids when
// the user runs a custom/bulk action while rows are still unsaved. A custom
// action operates on PERSISTED records, so the pending edits must first be
// committed (Save) or thrown away (Discard) — or the action abandoned (Cancel).
// Three explicit choices, so unlike a yes/no window.confirm this needs a modal.
//
// Pure UI: it only reports the user's choice. The host owns what Save / Discard
// actually do (its records API differs per surface), then runs the action.

import { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { tw } from '../testHooks'

export type SaveDiscardChoice = 'save' | 'discard' | 'cancel'

interface PromptInfo {
  // What the user is about to run, woven into the message (e.g. an action name).
  actionLabel?: string
  // How many rows have unsaved changes (shown for context).
  pendingCount?: number
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 12000,
}

function SaveDiscardModal({
  info, onChoose,
}: { info: PromptInfo; onChoose: (c: SaveDiscardChoice) => void }) {
  if (typeof document === 'undefined') return null
  const n = info.pendingCount || 0
  const changes = n > 0
    ? `${n} unsaved change${n === 1 ? '' : 's'}`
    : 'unsaved changes'
  const run = info.actionLabel ? `running “${info.actionLabel}”` : 'running this action'
  const btn: React.CSSProperties = {
    padding: '6px 14px', fontSize: '0.8625rem', borderRadius: 6, cursor: 'pointer',
  }
  return createPortal(
    <div style={overlayStyle} onClick={() => onChoose('cancel')}>
      <div
        onClick={e => e.stopPropagation()}
        data-tw-dialog="save_discard"
        style={{
          background: 'var(--color-surface, #fff)', color: 'var(--color-text, #111)',
          borderRadius: 8, width: 'min(440px, 94vw)', overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}>
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid var(--color-border, #e5e5e5)',
          fontWeight: 600, fontSize: '0.9625rem',
        }}>Unsaved changes</div>
        <div style={{ padding: '14px 16px', fontSize: '0.9225rem', lineHeight: 1.5 }}>
          You have {changes}. Save them before {run}? The action runs on the
          saved records, so pending edits must be committed or discarded first.
        </div>
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '10px 16px', borderTop: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <button
            onClick={() => onChoose('cancel')}
            {...tw('action', 'cancel')}
            style={{ ...btn, border: '1px solid var(--color-border, #ccc)', background: 'transparent', color: 'var(--color-text, #111)' }}
          >Cancel</button>
          <button
            onClick={() => onChoose('discard')}
            {...tw('action', 'discard')}
            style={{ ...btn, border: '1px solid var(--color-danger, #dc2626)', background: 'transparent', color: 'var(--color-danger, #dc2626)' }}
          >Discard changes</button>
          <button
            onClick={() => onChoose('save')}
            {...tw('action', 'save')}
            style={{ ...btn, border: 'none', background: 'var(--color-accent, #4c8dff)', color: '#fff' }}
          >Save &amp; continue</button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

interface Pending {
  info: PromptInfo
  resolve: (c: SaveDiscardChoice) => void
}

/**
 * Drive the unsaved-changes gate. `guard(info)` opens the modal and resolves
 * with the user's choice ('save' | 'discard' | 'cancel'); render
 * `saveDiscardModal` in the host tree. The host then performs save/discard with
 * its own records API and proceeds (or aborts on 'cancel').
 */
export function useSaveDiscardGuard(): {
  guard: (info?: PromptInfo) => Promise<SaveDiscardChoice>
  saveDiscardModal: React.ReactNode
} {
  const [pending, setPending] = useState<Pending | null>(null)
  const guard = useCallback((info: PromptInfo = {}) =>
    new Promise<SaveDiscardChoice>((resolve) => setPending({ info, resolve })), [])
  const choose = (c: SaveDiscardChoice) =>
    setPending((p) => { p?.resolve(c); return null })
  const saveDiscardModal = pending
    ? <SaveDiscardModal info={pending.info} onChoose={choose} />
    : null
  return { guard, saveDiscardModal }
}

export default SaveDiscardModal
